import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getVipTierBenefitsApi, postVipApplicationApi, postVipSourceOfProofApi } from '@flipster/apis/v1/vip';
import { PresignedPost, TierBenefit } from '@flipster/apis/v1/vip/types';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { RouterScope } from '@flipster/react/providers/route';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';
import Num from '@flipster/utils/num';

export interface UploadedFile {
  file: File;
  preview: string;
}

const maxUsernameLength = 32;
const maxFileCount = 3;
const maxFileSizeInMB = 10;
const maxFileSizeInBytes = maxFileSizeInMB * 1024 * 1024;
const acceptedFileTypes = ['image/png', 'image/jpg', 'image/jpeg'];

export type FileValidationError = 'max_files_exceeded' | 'invalid_file_type' | 'file_too_large';

export const vipProgramPageBunja = bunja(() => {
  const router = bunja.use(RouterScope);
  const store = bunja.use(JotaiStoreScope);

  // VIP tier benefits query atom
  const vipTierBenefitsQueryAtom = atomWithQuery(() => ({
    queryKey: ['getVipTierBenefitsApi'],
    queryFn: getVipTierBenefitsApi,
  }));

  const vipTierBenefitsAtom = atom((get) => get(vipTierBenefitsQueryAtom).data?.data);

  const maxMakerDiscountAtom = atom((get) => {
    const benefits = get(vipTierBenefitsAtom);
    if (!benefits) return 0;
    return getMaxDiscount(benefits.vipTiers, 'pswapMakerFee');
  });

  const maxTakerDiscountAtom = atom((get) => {
    const benefits = get(vipTierBenefitsAtom);
    if (!benefits) return 0;
    return getMaxDiscount(benefits.vipTiers, 'pswapTakerFee');
  });

  // Form state atoms
  const telegramUsernameAtom = atom('');
  const uploadedFilesAtom = atom<UploadedFile[]>([]);
  const isAgreedAtom = atom(false);
  const showErrorsAtom = atom(false);
  const isSubmittingAtom = atom(false);

  // Derived atoms
  const isFormValidAtom = atom((get) => {
    const telegramUsername = get(telegramUsernameAtom);
    const uploadedFiles = get(uploadedFilesAtom);
    const isAgreed = get(isAgreedAtom);
    return (
      telegramUsername.trim().length > 0 &&
      telegramUsername.trim().length <= maxUsernameLength &&
      uploadedFiles.length > 0 &&
      isAgreed
    );
  });

  const canUploadMoreAtom = atom((get) => {
    return get(uploadedFilesAtom).length < maxFileCount;
  });

  // Field-specific error atoms (only show when showErrors is true)
  const usernameErrorAtom = atom((get) => {
    if (!get(showErrorsAtom)) return false;
    return get(telegramUsernameAtom).trim().length === 0;
  });

  const usernameTooLongErrorAtom = atom((get) => {
    return get(telegramUsernameAtom).trim().length > maxUsernameLength;
  });

  const uploadErrorAtom = atom((get) => {
    if (!get(showErrorsAtom)) return false;
    return get(uploadedFilesAtom).length === 0;
  });

  const consentErrorAtom = atom((get) => {
    if (!get(showErrorsAtom)) return false;
    return !get(isAgreedAtom);
  });

  // Functions
  function validateAndAddFiles(files: FileList | File[]): FileValidationError | null {
    const currentFiles = store.get(uploadedFilesAtom);
    const availableSlots = maxFileCount - currentFiles.length;

    if (availableSlots <= 0) return 'max_files_exceeded';
    const filesToAdd: UploadedFile[] = [];
    let error: FileValidationError | null = null;
    for (const file of Array.from(files)) {
      if (filesToAdd.length >= availableSlots) {
        error = 'max_files_exceeded';
        break;
      }

      if (!acceptedFileTypes.includes(file.type)) {
        error = 'invalid_file_type';
        continue;
      }

      if (file.size > maxFileSizeInBytes) {
        error = 'file_too_large';
        continue;
      }

      filesToAdd.push({ file, preview: URL.createObjectURL(file) });
    }

    if (filesToAdd.length > 0) store.set(uploadedFilesAtom, [...currentFiles, ...filesToAdd]);
    return error;
  }

  function removeFile(index: number) {
    const currentFiles = store.get(uploadedFilesAtom);
    const fileToRemove = currentFiles[index];
    if (fileToRemove) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    store.set(
      uploadedFilesAtom,
      currentFiles.filter((_, i) => i != index),
    );
  }

  function triggerValidation(): boolean {
    store.set(showErrorsAtom, true);
    return store.get(isFormValidAtom);
  }

  async function submitApplication() {
    const isValid = triggerValidation();
    if (!isValid) return;

    const uploadedFiles = store.get(uploadedFilesAtom);
    const telegramUsername = store.get(telegramUsernameAtom);

    store.set(isSubmittingAtom, true);

    try {
      // 1. Get presigned URLs for all files
      const { data: presignedData } = await postVipSourceOfProofApi({
        numberOfFiles: uploadedFiles.length,
      });

      // 2. Upload all files to S3 using presigned URLs
      const uploadedUrls = await Promise.all(
        uploadedFiles.map((uploadedFile, index) =>
          uploadFileToPresignedUrl(uploadedFile.file, presignedData.presignedPosts[index]),
        ),
      );

      // 3. Submit the VIP application with uploaded file URLs
      await postVipApplicationApi({
        snsName: 'TELEGRAM',
        snsAccountInfo: { username: telegramUsername.trim() },
        sourceOfProof: uploadedUrls,
      });

      // 4. Reset form on success
      uploadedFiles.forEach((file) => URL.revokeObjectURL(file.preview));
      store.set(telegramUsernameAtom, '');
      store.set(uploadedFilesAtom, []);
      store.set(isAgreedAtom, false);
      store.set(showErrorsAtom, false);

      router.push('/vip-program/success');
    } catch (error) {
      router.push('/vip-program/failed');
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      store.set(isSubmittingAtom, false);
    }
  }

  return {
    // VIP tier benefits
    vipTierBenefitsQueryAtom,
    vipTierBenefitsAtom,
    maxMakerDiscountAtom,
    maxTakerDiscountAtom,
    // Form state
    telegramUsernameAtom,
    uploadedFilesAtom,
    isAgreedAtom,
    isFormValidAtom,
    isSubmittingAtom,
    canUploadMoreAtom,
    usernameErrorAtom,
    usernameTooLongErrorAtom,
    uploadErrorAtom,
    consentErrorAtom,
    validateAndAddFiles,
    removeFile,
    triggerValidation,
    submitApplication,
    maxFileCount,
    maxFileSizeInMB,
    maxUsernameLength,
  };
});
vipProgramPageBunja.debugLabel = 'vipProgramPageBunja';

function getMaxDiscount(tiers: TierBenefit[], feeKey: 'pswapMakerFee' | 'pswapTakerFee'): number {
  const baseTier = tiers.find((t) => t.vipTier === 0);
  if (!baseTier) return 0;
  const baseFee = Num(baseTier[feeKey]);
  if (baseFee.lte(0)) return 0;
  let max = 0;
  for (const tier of tiers) {
    const discount = Math.round(Num(1).minus(Num(tier[feeKey]).div(baseFee)).times(100).toNumber());
    if (discount > max) max = discount;
  }
  return max;
}

async function uploadFileToPresignedUrl(file: File, presignedPost: PresignedPost): Promise<string> {
  const formData = new FormData();
  // Add all presigned fields first
  const key = presignedPost.fields['key'];
  Object.entries(presignedPost.fields).forEach(([key, value]) => formData.append(key, value));
  // Add Content-Type
  formData.append('Content-Type', file.type);
  // Add the file last (required by S3)
  formData.append('file', renameFile(file, key.split('/').at(-1) ?? file.name));
  const response = await fetch(presignedPost.url, { method: 'POST', body: formData });
  if (!response.ok) throw new Error(`Failed to upload file: ${response.statusText}`);
  return key;
}

function renameFile(file: File, name: string): File {
  const ext = file.name.split('.').at(-1);
  return new File([file], `${name}.${ext}`, { type: file.type });
}
